import React, { useState } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Tag } from '@frontend/app/refresh-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faPaintbrush } from '@fortawesome/free-solid-svg-icons';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
} from '@frontend/shadcn/components/ui/alert-dialog';
import { TriangleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { ProjectByIdQuery_project_onboardingTemplates } from '@frontend/app/queries/types/ProjectByIdQuery';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { useHistory, useLocation } from 'react-router-dom';
import { logger } from '@common';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { cn } from '@frontend/shadcn/lib/utils';
import { useSaveProjectCampaign } from '@frontend/app/hooks';
import { useDeleteProgramOnboardingTemplate } from '../hooks/useDeleteProgramOnboardingTemplate';
import { useDuplicateProgramOnboardingTemplate } from '../hooks/useDuplicateProgramOnboardingTemplate';
import { SettingsRoutes } from '../../../constants';

const amplifyTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/amplifyTemplate.png';
const pulseTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/pulseTemplate.png';

interface ApplicationProps {
  application: ProjectByIdQuery_project_onboardingTemplates;
  onUpdate: () => void;
  projectId: number;
  onOpenMarketplace?: (templateId?: number) => void;
  publishedApplicationId?: number;
}

export function Application(props: ApplicationProps) {
  const { application, onUpdate, projectId, onOpenMarketplace, publishedApplicationId } = props;
  const history = useHistory();
  const location = useLocation();
  const [deleteProgramOnboardingTemplate] = useDeleteProgramOnboardingTemplate();
  const [duplicateProgramOnboardingTemplate] = useDuplicateProgramOnboardingTemplate();
  const [saveProjectCampaign] = useSaveProjectCampaign();
  const { toast } = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const onClickDeleteApplication = async () => {
    try {
      await deleteProgramOnboardingTemplate({
        variables: {
          id: application.id,
        },
      });
      toast({
        title: 'Application deleted successfully',
        variant: 'success',
        duration: 1000,
        className: cn('top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4'),
      });
      setIsDeleteDialogOpen(false);
    } catch (error) {
      logger.error('Error deleting program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  const onClickOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const onClickDuplicateApplication = async () => {
    try {
      await duplicateProgramOnboardingTemplate({
        variables: {
           projectId,
          templateId: application.id,
        },
      });
    } catch (error) {
      logger.error('Error duplicating program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  const onClickViewApplication = () => {
    history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', application.id.toString()));
  };

  const onClickCustomizeApplication = () => {
    history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', application.id.toString()));
  };

  const onClickDelistFromMarketplace = async () => {
    try {
      await saveProjectCampaign({
        variables: {
          projectId,
          campaign: {
            external_listing_url: null,
            status: 'UNPUBLISH'
          },
        },
      });
    } catch (error) {
      logger.error('Error delisting program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  const onClickPublishToMarketplace = async () => {
    // Add templateId to URL and open marketplace drawer
    if (onOpenMarketplace) {
      // Add templateId to URL as query parameter using React Router
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('templateId', application.id.toString());
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString()
      });

      // Open marketplace drawer
      onOpenMarketplace(application.id);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="flex p-4 justify-between items-center">
        {/* Left Content */}
        <div className="flex flex-col flex-1 gap-4">
          {/* Top Section */}
          <div className="flex flex-col gap-2">
            {publishedApplicationId === application.id && (
              <Tag
                label="Published to Creator Marketplace"
                bgColor="bg-green-2"
                textColor="text-green-6"
                className="w-fit"
              />
            )}
            <div className="flex flex-col gap-0">
              <h3 className="text-lg font-medium text-grey-6 m-0">{application.applicationTitle}</h3>
            </div>
          </div>

          {/* Bottom Section - Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-2" onClick={onClickCustomizeApplication}>
              <FontAwesomeIcon icon={faPaintbrush} />
              Customize
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open menu">
                  <FontAwesomeIcon icon={faEllipsis} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-sm" onClick={onClickViewApplication}>
                  View Application
                </DropdownMenuItem>
                {publishedApplicationId === application.id ? (
                  <DropdownMenuItem className="text-sm" onClick={onClickDelistFromMarketplace}>
                    Delist from Marketplace
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem className="text-sm" onClick={onClickPublishToMarketplace}>
                    Publish to Marketplace
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="text-sm" onClick={onClickDuplicateApplication}>
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-sm text-red-5 focus:bg-red-50" onClick={onClickOpenDeleteDialog}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Right Content - Image Placeholder */}
        <div className="w-[130px] h-[130px] bg-grey-1 border border-grey-2 rounded-2xl flex items-center justify-center">
          {/* <Skeleton className="w-12 h-24 bg-grey-3 rounded" /> */}
          <img
            src={application.templateType === ProgramOnboardingTemplateType.AMPLIFY ? amplifyTemplate : pulseTemplate}
            alt={application.templateType}
            className="w-[48px] h-[96px] object-contain"
          />
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="w-[28rem] p-8 bg-white rounded-2xl shadow-[0px_2px_6px_-4px_rgba(0,0,0,0.12)] shadow-[0px_4px_8px_0px_rgba(93,105,116,0.08)] shadow-[0px_8px_24px_8px_rgba(93,105,116,0.05)]">
          <AlertDialogHeader className="space-y-0">
            <div className="flex-1 inline-flex flex-col justify-start items-end gap-2">
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="w-6 h-6 relative">
                  <TriangleExclamationIcon className="w-6 h-6 text-red-5" />
                </div>
                <div className="flex-1 justify-start text-grey-6 text-base font-medium leading-7">
                  Permanently Delete Application Page?
                </div>
              </div>
              <div className="self-stretch justify-start text-grey-5 text-sm font-normal leading-normal">
                Deleting this application page will permanently remove it from your campaign. All related invite links will be deactivated.
              </div>
              <div className="pt-6 inline-flex justify-end items-center gap-2">
                <AlertDialogCancel className="px-4 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-grey-3 inline-flex justify-center items-center text-grey-6 text-sm font-medium leading-normal hover:bg-grey-1">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={onClickDeleteApplication}
                  className="px-4 py-1 bg-red-5 rounded-lg inline-flex justify-center items-center text-white text-sm font-medium leading-normal hover:bg-red-6"
                >
                  Delete
                </AlertDialogAction>
              </div>
            </div>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
